import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Video, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { author: "", role_it: "", role_en: "", text_it: "", text_en: "", youtube_url: "", kind: "text", order: 0, active: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/testimonials/${editing.id}`, editing);
        toast.success("Aggiornata");
      } else {
        await api.post("/admin/testimonials", editing);
        toast.success("Creata");
      }
      setEditing(null);
      load();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Confermi?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Eliminata");
    load();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-testimonials-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Voci del roster</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Testimonianze</h1>
        </div>
        <button className="btn-cta" onClick={() => setEditing({ ...EMPTY })} data-testid="admin-testi-add">
          <Plus size={16} /> Nuova testimonianza
        </button>
      </div>

      {loading ? <div className="text-[#0A0A0A]/50">Caricamento…</div> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="border-2 border-[#0A0A0A] p-5 bg-[#FAFAF7]" data-testid={`admin-testi-${t.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {t.kind === "video" ? <Video size={16} className="text-[#FF5A1F]" /> : <MessageSquareQuote size={16} className="text-[#FF5A1F]" />}
                  <div className="font-display text-xl uppercase">{t.author}</div>
                </div>
                {!t.active && <span className="text-[10px] uppercase text-[#0A0A0A]/40">Nascosta</span>}
              </div>
              <div className="text-xs uppercase tracking-widest text-[#0A0A0A]/60">{t.role_it}</div>
              {t.kind === "video" ? (
                <div className="mt-3 text-xs text-[#0A0A0A]/60 break-all">{t.youtube_url}</div>
              ) : (
                <p className="mt-3 text-sm text-[#0A0A0A]/70 line-clamp-3">{t.text_it}</p>
              )}
              <div className="mt-4 flex gap-2">
                <button className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest flex items-center gap-1 hover:bg-[#0A0A0A] hover:text-[#FAFAF7]" onClick={() => setEditing(t)} data-testid={`admin-testi-edit-${t.id}`}>
                  <Pencil size={12} /> Modifica
                </button>
                <button className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest hover:bg-red-600 hover:text-white hover:border-red-600 flex items-center gap-1" onClick={() => remove(t.id)} data-testid={`admin-testi-delete-${t.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="admin-testi-editor">
          <div className="bg-[#FAFAF7] max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex items-center justify-between">
              <h3 className="font-display text-2xl uppercase">{editing.id ? "Modifica" : "Nuova"} testimonianza</h3>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs uppercase font-bold tracking-widest text-[#0A0A0A]/60">Tipo</span>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => setEditing({ ...editing, kind: "text" })} className={`px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase font-bold tracking-widest ${editing.kind === "text" ? "bg-[#0A0A0A] text-[#FAFAF7]" : ""}`} data-testid="testi-kind-text">Testo</button>
                  <button type="button" onClick={() => setEditing({ ...editing, kind: "video" })} className={`px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase font-bold tracking-widest ${editing.kind === "video" ? "bg-[#0A0A0A] text-[#FAFAF7]" : ""}`} data-testid="testi-kind-video">Video YouTube</button>
                </div>
              </label>

              <div className="grid md:grid-cols-2 gap-4">
                <Input label="Autore" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testId="testi-author" />
                <Input label="Ordine" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v) || 0 })} testId="testi-order" />
                <Input label="Ruolo (IT)" value={editing.role_it} onChange={(v) => setEditing({ ...editing, role_it: v })} testId="testi-role-it" />
                <Input label="Ruolo (EN)" value={editing.role_en} onChange={(v) => setEditing({ ...editing, role_en: v })} testId="testi-role-en" />
              </div>

              {editing.kind === "video" ? (
                <Input label="URL YouTube" value={editing.youtube_url} onChange={(v) => setEditing({ ...editing, youtube_url: v })} placeholder="https://www.youtube.com/watch?v=..." testId="testi-yt" />
              ) : (
                <>
                  <TextArea label="Testo (IT)" value={editing.text_it} onChange={(v) => setEditing({ ...editing, text_it: v })} testId="testi-text-it" />
                  <TextArea label="Testo (EN)" value={editing.text_en} onChange={(v) => setEditing({ ...editing, text_en: v })} testId="testi-text-en" />
                </>
              )}
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="testi-active" />
                <span className="text-sm">Visibile sul sito</span>
              </label>
            </div>
            <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline-dark" data-testid="testi-cancel">Annulla</button>
              <button onClick={save} className="btn-cta" data-testid="testi-save">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", testId, placeholder }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white" />
  </label>
);
const TextArea = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId} className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white resize-none" />
  </label>
);
